// ════════════════════════════════════════════════════════════════════
// src/storeVisits/storeVisitView.js
//
// READ-ONLY store-visit report view + role-based "Visits" screen dispatcher.
// Net-new for the staff-app Visits TAB (replaces the floating button).
//
//   Area managers / admins → launcher ("New Store Visit" → showVisitChooser)
//                            + their recent visits (read-only, tappable).
//   Store / kiosk staff    → read-only list of THIS store's submitted reports.
//
// All read-only. No edit controls anywhere in this module.
//
// Endpoints used (all behind requireAdmin = token-valid, not role-gated):
//   GET /api/store-visits?store=<name>   — lightweight list (no section_data)
//   GET /api/store-visits/<id>           — full row incl. section_data
//
// Re: `DB` reference — bare `DB`, not window.DB (db.js uses let/const, lives
// in shared script scope). Re: `currentUser` — same: bare global in the
// staff app, read defensively via typeof.
// ════════════════════════════════════════════════════════════════════

import { SECTIONS } from './form-spec.js';
import { injectSvrStyles, svrHeaderHtml } from './svrChrome.js';
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

const RAG = {
  green: { bg: '#10b981', label: 'Green' },
  amber: { bg: '#f59e0b', label: 'Amber' },
  red:   { bg: '#ef4444', label: 'Red'   },
};

const STATUS_LABEL = {
  draft:     { text: 'Draft',     bg: '#9ca3af' },
  submitted: { text: 'Submitted', bg: '#3b82f6' },
  reviewed:  { text: 'Reviewed',  bg: '#10b981' },
};

function getToken() {
  return adminToken();   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
}

function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') {
    return DB.workerUrl();
  }
  return '';
}

function getCurrentUser() {
  return (typeof currentUser !== 'undefined' && currentUser) ? currentUser : null;
}

function isAreaUser() {
  const u = getCurrentUser();
  return !!u && (u.role === 'area_manager' || u.role === 'admin');
}

// Who may EDIT a completed report: area managers + head office only (same gate as
// delete). Works in either host app — the admin app sets `currentAdmin`, the staff
// app sets `currentUser`; store managers / store logins get neither role and so
// never see the Edit control. v126.1
function canEditReports() {
  const a = (typeof currentAdmin !== 'undefined' && currentAdmin) ? currentAdmin : null;
  const u = getCurrentUser();
  const who = a || u;
  return !!who && (who.role === 'admin' || who.role === 'area_manager');
}

// v135.1: English or Arabic for a confirm or alert box, which the page translator never sees.
function tr(s) { return (typeof I18N !== 'undefined' && I18N.t) ? I18N.t(s) : s; }
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Dubai-correct date display — NEVER naive toISOString.
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Asia/Dubai',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return String(iso);
  }
}

function ragPill(rag) {
  const r = RAG[rag];
  if (!r) return '<span style="font-size:12px;color:var(--sub,#6b7280);">No rating</span>';
  return '<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:12px;' +
    'font-weight:700;color:#fff;background:' + r.bg + ';">' + r.label + '</span>';
}

function statusPill(status) {
  const s = STATUS_LABEL[status] || { text: status || 'Unknown', bg: '#9ca3af' };
  return '<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:11px;' +
    'font-weight:700;color:#fff;background:' + s.bg + ';">' + escapeHtml(s.text) + '</span>';
}

// v133.100: what the STORE has done with a submitted report, beside the status. The status never moves past
// "Submitted" (nothing sets 'reviewed'), so an area manager read the pill as "not done" even when the store
// had acknowledged it and closed every task (Dubai Mall, 10 Oct 2026). Pure: -> { text, bg } or null.
export function storeStage(v) {
  if (!v || v.status !== 'submitted' || !v.store_actioned_at) return null;
  const updates = Array.isArray(v.action_updates) ? v.action_updates : [];
  // the same three states and words as the list's actionedChip (storeVisitReports.js)
  if (updates.some((u) => u && u.kind === 'declined')) return { text: '⛔ Declined', bg: '#ef4444' };
  if (updates.some((u) => u && (u.all_done || u.kind === 'completed'))) return { text: '✅ Completed', bg: '#059669' };
  return { text: '✓ Acknowledged', bg: '#10b981' };
}
function storeStagePill(v) {
  const s = storeStage(v);
  return s ? ' <span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:11px;' +
    'font-weight:700;color:#fff;background:' + s.bg + ';">' + escapeHtml(s.text) + '</span>' : '';
}

// Where the AM completed the report (geotag), if captured. A Google Maps link rather
// than an embedded map — keeps the bundle light and works on the SW-cached app.
function geoChip(v) {
  if (!v || typeof v.geo_lat !== 'number' || typeof v.geo_lng !== 'number') return '';
  const coords = v.geo_lat.toFixed(5) + ', ' + v.geo_lng.toFixed(5);
  const acc = (typeof v.geo_accuracy === 'number') ? (' · ±' + Math.round(v.geo_accuracy) + 'm') : '';
  const maps = 'https://www.google.com/maps?q=' + encodeURIComponent(v.geo_lat + ',' + v.geo_lng);
  return '<div style="display:flex;align-items:center;gap:8px;margin-bottom:16px;font-size:13px;color:var(--text);">' +
    '<span title="Where this report was submitted">📍</span>' +
    '<a href="' + maps + '" target="_blank" rel="noopener" style="color:var(--info-deep);text-decoration:none;font-weight:600;">' +
      escapeHtml(coords) + '</a>' +
    '<span style="color:var(--tiny);">' + escapeHtml(acc) + '</span>' +
  '</div>';
}

// ── Data ──────────────────────────────────────────────────────────────

// Send the admin token if we have one (area/HO see all); store logins have no
// token and read their own store via ?store= — SVRs are not confidential.
function authHeaders() {
  const token = getToken();
  return token ? { 'Authorization': 'Bearer ' + token } : {};
}

async function fetchVisits(store) {
  let q = '/api/store-visits';
  if (store) q += '?store=' + encodeURIComponent(store);
  const res = await fetch(getWorkerUrl() + q, { headers: authHeaders() });
  if (res.status === 401 || res.status === 403) throw new Error('NOT_AUTHED');
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error('Load failed: ' + res.status + ' ' + t.slice(0, 120));
  }
  return await res.json();
}

async function fetchVisitDetail(id) {
  let q = '/api/store-visits/' + encodeURIComponent(id);
  // Store logins carry no token: scope the read to their own store so the worker
  // can confirm the report belongs to this store (and is a completed one) before
  // returning it. Admins / area managers send a Bearer token and get unrestricted
  // access, so they don't need the store param.
  if (!getToken()) {
    const u = getCurrentUser();
    if (u && u.store) q += '?store=' + encodeURIComponent(u.store);
  }
  const res = await fetch(getWorkerUrl() + q, {
    headers: authHeaders(),
  });
  if (res.status === 401 || res.status === 403) throw new Error('NOT_AUTHED');
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error('Load failed: ' + res.status + ' ' + t.slice(0, 120));
  }
  return await res.json();
}

// ── Card list (read-only) ───────────────────────────────────────────────

function visitCard(v) {
  const when = fmtDate(v.entry_time || v.created_at);
  // v131.79 (Matt: "on the svr, show the area manager and staff members face").
  // visited_by_id is the AM ACCOUNT id (storeVisitReports.js draft-ownership
  // check relies on exactly that), so the face is a chip the sweep hydrates.
  // Older rows recorded only the NAME - those keep initials, which is the
  // honest rendering: we do not know WHICH account it was.
  const whoFace = (v.visited_by_id != null && typeof Avatars !== 'undefined' && Avatars.chip)
    ? Avatars.chip('account', v.visited_by_id, v.visited_by_name, 18) + ' '
    : '';
  const who = whoFace + escapeHtml(v.visited_by_name || 'Unknown');
  const type = v.visit_type === 'short' ? 'Short visit' : 'Detailed visit';
  const store = escapeHtml(v.store || 'Unknown store');
  const plan = (v.action_plan || '').trim();
  const planHtml = plan
    ? '<div style="margin-top:8px;font-size:13px;color:var(--text,#111);line-height:1.45;">' +
        '<span style="font-weight:700;color:var(--sub,#6b7280);">Action plan: </span>' +
        '<span translate="no">' + escapeHtml(plan) + '</span></div>'
    : '';
  // A draft opened by an area manager / HO RESUMES in the edit form (where Submit +
  // Delete live, v124.1) rather than the read-only detail — so signpost it differently.
  const isDraft = v.status === 'draft';
  const cta = isDraft ? 'Continue draft →' : 'View full report →';
  const ctaColor = isDraft ? 'var(--ui-warn)' : 'var(--info-deep)';   // amber for the resumable draft
  return '<button type="button" class="svr-view-card" data-id="' + escapeHtml(v.id) + '" ' +
    'style="display:block;width:100%;text-align:start;cursor:pointer;background:var(--card,#fff);' +
    'border:1px solid var(--border,#e5e7eb);border-radius:12px;padding:14px;margin-bottom:10px;">' +
      '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">' +
        '<div style="font-weight:800;font-size:15px;line-height:1.25;">' + store + '</div>' +
        ragPill(v.overall_rag) +
      '</div>' +
      '<div style="margin-top:4px;font-size:12px;color:var(--sub,#6b7280);">' +
        escapeHtml(when) + ' · ' + type + ' · ' + who + ' ' + statusPill(v.status) +
      '</div>' +
      planHtml +
      '<div style="margin-top:8px;font-size:12px;color:' + ctaColor + ';font-weight:600;">' + cta + '</div>' +
    '</button>';
}

function renderCardList(visits, container) {
  if (!Array.isArray(visits) || visits.length === 0) {
    container.innerHTML = '<div style="padding:24px 4px;color:var(--sub,#6b7280);font-size:14px;">' +
      'No reports yet.</div>';
    return;
  }
  container.innerHTML = visits.map(visitCard).join('');
  const cards = container.querySelectorAll('.svr-view-card');
  visits.forEach(function (v, i) {
    const btn = cards[i];
    if (!btn) return;
    btn.addEventListener('click', function () {
      // A DRAFT opened by an area manager / HO resumes in the edit form so it can be
      // finished, submitted or deleted (the Submit/Delete controls added in v124.1) —
      // otherwise a draft is stuck forever behind a read-only view. Pass no preloaded
      // row so the launcher refetches the FULL draft (the list projection may omit
      // section_data). Everyone else, and every non-draft, opens the read-only detail.
      if (v.status === 'draft' && canEditReports() && typeof window.launchEditVisitForm === 'function') {
        window.launchEditVisitForm(v.id);
      } else {
        openVisitDetail(v.id);
      }
    });
  });
}

// ── Full read-only detail overlay ───────────────────────────────────────

function detailRagRow(label, value) {
  return '<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;' +
    'padding:8px 0;border-bottom:1px solid var(--border,#f0f0f0);">' +
      '<div style="font-size:13px;color:var(--text,#111);flex:1;">' + escapeHtml(label) + '</div>' +
      ragPill(value) +
    '</div>';
}

function detailSection(section, data) {
  const d = (data && data[section.key]) || {};
  const rows = section.questions
    .filter(function (q) { return q.type === 'rag' || q.type === 'shift-embed'; })
    .map(function (q) { return detailRagRow(q.label, d[q.key]); })
    .join('');
  // gather any free-text comments in this section
  const comments = section.questions
    .filter(function (q) { return q.type === 'comment' && (d[q.key] || '').trim(); })
    .map(function (q) {
      return '<div style="margin-top:8px;font-size:13px;color:var(--sub,#6b7280);">' +
        '<span style="font-weight:700;">' + escapeHtml(q.label) + ' </span>' +
        '<span translate="no">' + escapeHtml(d[q.key]) + '</span></div>';
    }).join('');
  if (!rows && !comments) return '';
  return '<div style="margin-bottom:18px;">' +
    '<div style="font-weight:700;font-size:14px;margin-bottom:4px;">' + escapeHtml(section.title) + '</div>' +
    rows + comments +
  '</div>';
}

function sectionTitle(key, sections) {
  const list = (Array.isArray(sections) && sections.length) ? sections : SECTIONS;
  const s = list.find(function (x) { return x.key === key; });
  return s ? s.title : (key || 'Photo');
}

async function fetchPhotos(visitId, store) {
  // v131.40: a tokenless reader names the visit's store (the Worker checks it); a Bearer reader may omit it.
  const storeQ = store ? '&store=' + encodeURIComponent(store) : '';
  const res = await fetch(getWorkerUrl() + '/api/store-visit-photos?visit_id=' + encodeURIComponent(visitId) + storeQ, { cache: 'no-store',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('photos ' + res.status);
  return await res.json();
}

function openLightbox(url) {
  const lb = document.createElement('div');
  lb.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.9);z-index:10002;' +
    'display:flex;align-items:center;justify-content:center;cursor:zoom-out;padding:16px;';
  const img = document.createElement('img');
  img.src = url;
  img.style.cssText = 'max-width:96%;max-height:96%;object-fit:contain;border-radius:4px;';
  lb.appendChild(img);
  lb.addEventListener('click', function () { if (lb.parentNode) lb.parentNode.removeChild(lb); });
  document.body.appendChild(lb);
}

function photosGridHtml(photos, sections) {
  const usable = (photos || []).filter(function (p) { return p && p.signed_url; });
  if (!usable.length) return '<div style="font-size:13px;color:var(--sub,#6b7280);">No photos uploaded.</div>';
  return '<div style="display:flex;flex-wrap:wrap;gap:10px;">' +
    usable.map(function (p) {
      return '<figure style="margin:0;width:96px;">' +
        '<img class="svr-photo-thumb" data-url="' + escapeHtml(p.signed_url) + '" src="' + escapeHtml(p.signed_url) + '" ' +
          'loading="lazy" style="width:96px;height:96px;object-fit:cover;border-radius:8px;' +
          'border:1px solid var(--border,#e5e7eb);cursor:zoom-in;display:block;">' +
        '<figcaption style="font-size:10px;color:var(--sub,#6b7280);margin-top:3px;line-height:1.2;' +
          'text-align:center;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' +
          escapeHtml(sectionTitle(p.section_key, sections)) + '</figcaption>' +
      '</figure>';
    }).join('') +
  '</div>';
}

async function loadPhotosInto(overlay, visitId, sections, store) {
  const slot = overlay.querySelector('#svr-detail-photos');
  if (!slot) return;
  try {
    const photos = await fetchPhotos(visitId, store);
    slot.innerHTML = photosGridHtml(photos, sections);
    slot.querySelectorAll('.svr-photo-thumb').forEach(function (img) {
      img.addEventListener('click', function () { openLightbox(img.dataset.url); });
    });
  } catch (e) {
    slot.innerHTML = '<div style="font-size:13px;color:var(--sub,#6b7280);">Couldn\u2019t load photos.</div>';
  }
}

// ── Read & actioned (store acknowledgement) ──────────────────────────────
// Tokenless write by design — store logins carry no token. The worker only
// stamps store_actioned_at + actioned_by, so a forged ack is low-stakes and
// auditable. Admin viewers see the state but never the button.
function isAdminContext() {
  return typeof currentAdmin !== 'undefined' && !!currentAdmin;
}

async function markActioned(v, name) {
  const res = await fetch(getWorkerUrl() + '/api/store-visits/' + encodeURIComponent(v.id) + '/actioned', { cache: 'no-store',
    method: 'POST',
    headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
    body: JSON.stringify({ store: v.store, actioned_by: name || '' }),
  });
  if (!res.ok) throw new Error('actioned ' + res.status);
  return await res.json();
}

function actionedFooter(v) {
  // v129.83 (Matt's spec): the wording is "Read & acknowledged" — step 1 of the
  // store's response; Completed / Can't-do-this follow in the progress section.
  if (v.store_actioned_at) {
    const by = v.actioned_by ? (' by ' + escapeHtml(v.actioned_by)) : '';
    return '<div id="svr-actioned-box" style="background:var(--ok-bg);border:1px solid var(--learn-ok-line);border-radius:10px;padding:12px 14px;margin-bottom:18px;">' +
      '<div style="font-weight:700;font-size:13px;color:var(--ok-deep);">✓ Read &amp; acknowledged' + by + '</div>' +
      '<div style="font-size:12px;color:var(--ok-deep);margin-top:2px;">' + fmtDate(v.store_actioned_at) + '</div></div>';
  }
  if (isAdminContext()) {
    return '<div style="background:var(--card,#f9fafb);border:1px dashed var(--border,#d1d5db);border-radius:10px;padding:12px 14px;margin-bottom:18px;">' +
      '<div style="font-size:13px;color:var(--sub,#6b7280);">Not yet marked read &amp; acknowledged by the store.</div></div>';
  }
  return '<div id="svr-actioned-box" style="background:var(--card,#f9fafb);border:1px solid var(--border,#e5e7eb);border-radius:10px;padding:14px;margin-bottom:18px;">' +
    '<div style="font-weight:700;font-size:13px;margin-bottom:8px;">Confirm you have read this report</div>' +
    '<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">' +
      '<input id="svr-actioned-name" type="text" placeholder="Your name (optional)" ' +
        'style="flex:1;min-width:160px;padding:9px 10px;border:1px solid var(--border,#d1d5db);border-radius:8px;font-size:14px;">' +
      '<button type="button" id="svr-actioned-btn" style="background:#10b981;color:#fff;border:none;' +
        'border-radius:8px;padding:10px 16px;cursor:pointer;font-size:14px;font-weight:700;">✓ Read &amp; acknowledged</button>' +
    '</div></div>';
}

function wireActioned(overlay, v) {
  const btn = overlay.querySelector('#svr-actioned-btn');
  if (!btn) return;
  btn.addEventListener('click', function () {
    const nameEl = overlay.querySelector('#svr-actioned-name');
    const name = nameEl ? nameEl.value.trim() : '';
    btn.disabled = true; btn.textContent = 'Saving…';
    markActioned(v, name).then(function () {
      v.store_actioned_at = new Date().toISOString();
      v.actioned_by = name;
      const box = overlay.querySelector('#svr-actioned-box');
      if (box) box.outerHTML = actionedFooter(v);
    }).catch(function () {
      btn.disabled = false; btn.textContent = '✓ Read & acknowledged';
      alert(tr('Could not save — please try again.'));
    });
  });
}

// ── Action-plan PROGRESS updates (v127.4, Doha - Landmark Mall) ──────────
// After a report is actioned, tasks still take days — the store appends
// progress notes here (the AM + HO get a push per note; "all tasks completed"
// gets the louder one). Append-only; the AM's action_plan text never changes.
async function postActionUpdate(v, note, kind, name, photos) {
  const res = await fetch(getWorkerUrl() + '/api/store-visits/' + encodeURIComponent(v.id) + '/action-update', { cache: 'no-store',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ store: v.store, note: note, kind: kind || 'progress', by: name || '', photos: photos || [] }),
  });
  if (!res.ok) throw new Error('action-update ' + res.status);
  return await res.json();
}

// v129.83 — corrective-action photos ride the update (canvas-compressed, the
// house pattern: max 1280px, stepped JPEG, ~≤400KB each, cap 3).
let _svrUpdPhotos = [];
function svrCompressPhoto(file, cb) {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = function () {
    try {
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      let out = null;
      [0.72, 0.55, 0.4].some(function (q) { out = canvas.toDataURL('image/jpeg', q); return out.length <= 400000; });
      URL.revokeObjectURL(url);
      cb(out);
    } catch (e) { URL.revokeObjectURL(url); cb(null); }
  };
  img.onerror = function () { URL.revokeObjectURL(url); cb(null); };
  img.src = url;
}

function actionUpdatesSection(v) {
  if (!v.store_actioned_at) return '';   // progress starts once the report is acknowledged
  const updates = Array.isArray(v.action_updates) ? v.action_updates : [];
  const rows = updates.map(function (u) {
    // v129.83 — three reply kinds: progress (grey), completed (green), declined (red + the reason).
    const kind = (u && u.kind) || ((u && u.all_done) ? 'completed' : 'progress');
    const colour = kind === 'completed' ? '#10b981' : kind === 'declined' ? '#ef4444' : 'var(--border-strong)';
    const prefix = kind === 'completed' ? '✅ ' : kind === 'declined' ? '⛔ Not possible: ' : '';
    const photoTag = (u && u.photos > 0)
      ? '<span style="font-size:11px;color:var(--info-deep);font-weight:700;cursor:pointer;" onclick="StoreVisits.loadActionPhotos(\'' + String(v.id) + '\')"> · 📷 ' + u.photos + ' photo' + (u.photos === 1 ? '' : 's') + '</span>'
      : '';
    return '<div style="padding:8px 10px;border-inline-start:3px solid ' + colour + ';margin-bottom:6px;background:var(--card);border-radius:0 8px 8px 0;">' +
      '<div style="font-size:13px;line-height:1.45;white-space:pre-wrap;">' + prefix + '<span translate="no">' + escapeHtml((u && u.note) || '') + '</span></div>' +
      '<div style="font-size:11px;color:var(--sub,#6b7280);margin-top:2px;">' + fmtDate(u && u.ts) + ((u && u.by) ? ' · ' + escapeHtml(u.by) : '') + photoTag + '</div>' +
    '</div>';
  }).join('');
  const closed = updates.some(function (u) { return u && (u.all_done || u.kind === 'completed' || u.kind === 'declined'); });
  const closedLine = updates.some(function (u) { return u && u.kind === 'declined'; })
    ? '<div style="font-size:12px;color:var(--danger-deep);font-weight:700;">⛔ The store has said this can\'t be done — your Area Manager has been notified.</div>'
    : '<div style="font-size:12px;color:var(--ok-deep);font-weight:700;">✅ All tasks completed</div>';
  const form = closed
    ? closedLine
    : '<div id="svr-upd-form" style="margin-top:8px;">' +
        '<textarea id="svr-upd-note" rows="2" placeholder="What have you done? (it is tidied up automatically and sent to your Area Manager)" ' +
          'style="width:100%;box-sizing:border-box;padding:9px 10px;border:1px solid var(--border,#d1d5db);border-radius:8px;font-size:14px;font-family:inherit;"></textarea>' +
        '<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:6px;">' +
          '<input id="svr-upd-name" type="text" placeholder="Your name (optional)" ' +
            'style="flex:1;min-width:140px;padding:8px 10px;border:1px solid var(--border,#d1d5db);border-radius:8px;font-size:13px;">' +
          '<input id="svr-upd-photos" type="file" accept="image/*" multiple style="display:none">' +
          '<button type="button" id="svr-upd-photo-btn" style="background:var(--card);color:var(--text);border:1px dashed var(--border-strong);border-radius:8px;' +
            'padding:8px 12px;cursor:pointer;font-size:12.5px;font-weight:700;">📷 Photos</button>' +
          '<span id="svr-upd-photo-count" style="font-size:11.5px;color:var(--sub,#6b7280);"></span>' +
        '</div>' +
        '<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:8px;">' +
          '<button type="button" id="svr-upd-btn" style="background:#2563eb;color:#fff;border:none;border-radius:8px;' +
            'padding:9px 14px;cursor:pointer;font-size:13px;font-weight:700;">Send progress update</button>' +
          '<button type="button" id="svr-upd-done-btn" style="background:#10b981;color:#fff;border:none;border-radius:8px;' +
            'padding:9px 14px;cursor:pointer;font-size:13px;font-weight:700;">✔ Completed</button>' +
          '<button type="button" id="svr-upd-decline-btn" style="background:var(--card);color:var(--danger-deep);border:1px solid var(--learn-bad-edge);border-radius:8px;' +
            'padding:9px 14px;cursor:pointer;font-size:13px;font-weight:700;">⛔ Can\'t do this</button>' +
        '</div>' +
      '</div>';
  return '<div id="svr-upd-box" style="background:var(--card,#f9fafb);border:1px solid var(--border,#e5e7eb);border-radius:10px;padding:12px 14px;margin-bottom:18px;">' +
    '<div style="font-weight:700;font-size:13px;margin-bottom:6px;">Action plan progress</div>' +
    (rows || '<div style="font-size:12.5px;color:var(--sub,#6b7280);margin-bottom:4px;">No updates yet — write what you\'ve done (add photos of the fix), or say why it can\'t be done.</div>') +
    form +
    '<div id="svr-action-photo-slot"></div>' +
  '</div>';
}

// v129.83 — view the corrective-action photos (on demand, never in the list read).
export async function loadActionPhotos(visitId) {
  const slot = document.getElementById('svr-action-photo-slot');
  if (!slot) return;
  slot.innerHTML = '<div style="font-size:12px;color:var(--sub,#6b7280);padding:6px 0;">Loading photos…</div>';
  try {
    const store = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : '';
    const res = await fetch(getWorkerUrl() + '/api/store-visits/' + encodeURIComponent(visitId) + '/action-photos' +
      (store ? '?store=' + encodeURIComponent(store) : ''), { cache: 'no-store', headers: authHeaders() });
    const d = await res.json().catch(function () { return {}; });
    if (!res.ok) throw new Error((d && d.error) || 'load failed');
    const photos = (d && d.photos) || [];
    slot.innerHTML = photos.length
      ? photos.map(function (p) {
          return '<img src="' + escapeHtml(p.photo) + '" alt="' + escapeHtml(p.filename || 'photo') + '" ' +
            'style="max-width:100%;max-height:360px;border-radius:10px;border:1px solid var(--border);margin-top:8px;display:block;">';
        }).join('')
      : '<div style="font-size:12px;color:var(--sub,#6b7280);padding:6px 0;">No photos attached.</div>';
  } catch (e) {
    slot.innerHTML = '<div style="font-size:12px;color:var(--danger);padding:6px 0;">' + escapeHtml(e.message || 'Could not load photos') + '</div>';
  }
}

function wireActionUpdates(overlay, v) {
  const btn = overlay.querySelector('#svr-upd-btn');
  if (!btn) return;
  _svrUpdPhotos = [];
  // 📷 photo picking (compressed on the device; cap 3)
  const photoInput = overlay.querySelector('#svr-upd-photos');
  const photoBtn = overlay.querySelector('#svr-upd-photo-btn');
  const photoCount = overlay.querySelector('#svr-upd-photo-count');
  if (photoBtn && photoInput) {
    photoBtn.addEventListener('click', function () { photoInput.click(); });
    photoInput.addEventListener('change', function () {
      const files = Array.prototype.slice.call(photoInput.files || []).slice(0, 3 - _svrUpdPhotos.length);
      photoInput.value = '';
      files.forEach(function (f) {
        svrCompressPhoto(f, function (dataUrl) {
          if (!dataUrl) return;
          if (_svrUpdPhotos.length >= 3) return;
          _svrUpdPhotos.push({ filename: f.name || 'photo.jpg', data: dataUrl });
          if (photoCount) photoCount.textContent = _svrUpdPhotos.length + '/3 attached';
        });
      });
    });
  }
  // One submit path, three kinds (v129.83: progress / completed / declined).
  function send(kind, sendBtn, label) {
    const noteEl = overlay.querySelector('#svr-upd-note');
    const note = noteEl ? noteEl.value.trim() : '';
    if (!note) {
      if (noteEl) {
        noteEl.placeholder = kind === 'declined'
          ? 'Tell your Area Manager WHY it can\'t be done (e.g. no stock)…'
          : 'Write what you have done first…';
        noteEl.focus();
      }
      return;
    }
    if (kind === 'completed' && !window.confirm(tr('Mark the whole action plan COMPLETED? Your note' + (_svrUpdPhotos.length ? ' and photos' : '') + ' go to your Area Manager.'))) return;
    if (kind === 'declined' && !window.confirm(tr('Tell your Area Manager this can\'t be done? Your reason goes to them and Head Office.'))) return;
    const name = String((overlay.querySelector('#svr-upd-name') || {}).value || '').trim();
    sendBtn.disabled = true; sendBtn.textContent = 'Sending…';
    postActionUpdate(v, note, kind, name, _svrUpdPhotos).then(function (data) {
      // The server returns the SAVED entry — tidied wording included — so the
      // store always sees exactly what was sent (v129.83, Matt's AI-tidy ask).
      const saved = (data && data._update) || { ts: new Date().toISOString(), by: name || null, note: note, kind: kind, all_done: kind === 'completed', photos: _svrUpdPhotos.length };
      v.action_updates = (Array.isArray(v.action_updates) ? v.action_updates : []).concat([saved]);
      _svrUpdPhotos = [];
      if (data && data._photos_error && typeof alert === 'function') alert(data._photos_error);
      const box = overlay.querySelector('#svr-upd-box');
      if (box) { box.outerHTML = actionUpdatesSection(v); wireActionUpdates(overlay, v); }
    }).catch(function () {
      sendBtn.disabled = false; sendBtn.textContent = label;
      alert(tr('Could not send — please try again.'));
    });
  }
  btn.addEventListener('click', function () { send('progress', btn, 'Send progress update'); });
  const doneBtn = overlay.querySelector('#svr-upd-done-btn');
  if (doneBtn) doneBtn.addEventListener('click', function () { send('completed', doneBtn, '✔ Completed'); });
  const declineBtn = overlay.querySelector('#svr-upd-decline-btn');
  if (declineBtn) declineBtn.addEventListener('click', function () { send('declined', declineBtn, '⛔ Can\'t do this'); });
}

function renderDetailOverlay(v) {
  injectSvrStyles();
  const overlay = document.createElement('div');
  overlay.className = 'svr-page';
  overlay.style.cssText = 'position:fixed;inset:0;background:var(--bg);z-index:10000;' +
    'overflow:auto;padding:0;-webkit-overflow-scrolling:touch;';
  const sd = v.section_data || {};
  // Phase 2: render against the template the report was FILLED against (its frozen
  // snapshot), not the live one — so editing the template never rewrites history.
  // Legacy rows (pre-snapshot) have no template_snapshot → fall back to live SECTIONS,
  // which is correct since they were genuinely filled against it. v125.0
  const tplSections = (Array.isArray(v.template_snapshot) && v.template_snapshot.length)
    ? v.template_snapshot
    : SECTIONS;
  const sectionsHtml = (v.visit_type === 'short')
    ? ''
    : tplSections.map(function (s) { return detailSection(s, sd); }).join('');
  const plan = (v.action_plan || '').trim();
  // Area managers / head office get an Edit control to re-open the report and
  // correct it (store managers / store logins never see it). v126.1
  const canEdit = canEditReports();
  const editBtnHtml = canEdit
    ? '<button type="button" id="svr-detail-edit" style="min-height:34px;padding:0 14px;border:none;' +
        'background:#2563eb;color:#fff;border-radius:9px;font-size:13px;font-weight:700;font-family:inherit;' +
        'cursor:pointer;margin-inline-end:8px;">✎ Edit</button>'
    : '';
  overlay.innerHTML =
    svrHeaderHtml({
      title: escapeHtml(v.store || 'Store visit'),
      subtitle: fmtDate(v.entry_time || v.created_at) + (v.visited_by_name ? ' · ' + escapeHtml(v.visited_by_name) : ''),
      right: editBtnHtml +
        '<button type="button" id="svr-detail-close" class="svr-close" aria-label="Close">✕</button>',
    }) +
    '<div class="svr-wrap">' +
      '<div class="svr-card">' +
        '<div style="display:flex;align-items:center;gap:10px;margin-bottom:16px;">' +
          '<span style="font-weight:700;font-size:14px;">Overall:</span>' + ragPill(v.overall_rag) +
          statusPill(v.status) + storeStagePill(v) +   // v133.100
        '</div>' +
        geoChip(v) +
        (plan
          ? '<div style="background:var(--surface-3);border-radius:12px;padding:12px 14px;margin-bottom:16px;">' +
              '<div style="font-weight:700;font-size:13px;margin-bottom:4px;">Action plan</div>' +
              '<div translate="no" style="font-size:13px;line-height:1.5;white-space:pre-wrap;">' + escapeHtml(plan) + '</div>' +
            '</div>'
          : '') +
        sectionsHtml +
        '<div style="margin-bottom:4px;">' +
          '<div style="font-weight:700;font-size:14px;margin-bottom:8px;">Photos</div>' +
          '<div id="svr-detail-photos" style="font-size:13px;color:var(--sub,#6b7280);">Loading photos…</div>' +
        '</div>' +
      '</div>' +
      actionedFooter(v) +
      actionUpdatesSection(v) +
      '<div style="height:24px;"></div>' +
    '</div>';
  document.body.appendChild(overlay);
  const close = overlay.querySelector('#svr-detail-close');
  if (close) close.addEventListener('click', function () {
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  });
  // Edit → close this read-only view and open the form in edit mode. We already
  // have the full row (`v`), so pass it through to avoid a refetch.
  const editBtn = overlay.querySelector('#svr-detail-edit');
  if (editBtn) editBtn.addEventListener('click', function () {
    if (typeof window.launchEditVisitForm === 'function') {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      window.launchEditVisitForm(v.id, v);
    } else {
      alert(tr('The editor is still loading — please try again in a moment.'));
    }
  });
  wireActioned(overlay, v);
  wireActionUpdates(overlay, v);   // v127.4 — action-plan progress thread
  loadPhotosInto(overlay, v.id, tplSections, v.store);   // v131.40
}

async function openVisitDetail(id) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:var(--bg,#fff);z-index:10000;' +
    'display:flex;align-items:center;justify-content:center;color:var(--sub,#6b7280);';
  overlay.textContent = 'Loading report…';
  document.body.appendChild(overlay);
  try {
    const v = await fetchVisitDetail(id);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    renderDetailOverlay(v);
  } catch (e) {
    overlay.innerHTML = '<div style="text-align:center;padding:24px;">' +
      '<div style="margin-bottom:12px;">' +
        (e.message === 'NOT_AUTHED' ? 'Please sign in to view this report.' : 'Could not load this report.') +
      '</div>' +
      '<button type="button" id="svr-detail-err-close" style="border:1px solid var(--border,#d1d5db);' +
        'background:var(--card,#fff);border-radius:8px;padding:8px 16px;cursor:pointer;">Close</button></div>';
    const c = overlay.querySelector('#svr-detail-err-close');
    if (c) c.addEventListener('click', function () {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    });
  }
}

// ── Screen renderers ────────────────────────────────────────────────────

function loading(container, msg) {
  container.innerHTML = '<div style="padding:24px 4px;color:var(--sub,#6b7280);font-size:14px;">' +
    escapeHtml(msg || 'Loading…') + '</div>';
}

function authPrompt(container) {
  container.innerHTML = '<div style="padding:24px 4px;color:var(--sub,#6b7280);font-size:14px;">' +
    'Please sign in to view store visit reports.</div>';
}

// Store-staff view: read-only list of THIS store's submitted/reviewed reports.
export async function renderStoreVisitReports(store, container) {
  if (!container) return;
  if (!store) {
    container.innerHTML = '<div style="padding:24px 4px;color:var(--sub,#6b7280);font-size:14px;">' +
      'No store is set for this account.</div>';
    return;
  }
  loading(container, 'Loading reports for ' + store + '…');
  try {
    const all = await fetchVisits(store);
    // Store staff see completed reports only — not the AM's in-progress drafts.
    const visible = (all || []).filter(function (v) { return v.status && v.status !== 'draft'; });
    renderCardList(visible, container);
  } catch (e) {
    if (e.message === 'NOT_AUTHED') { authPrompt(container); return; }
    container.innerHTML = '<div style="padding:24px 4px;color:var(--ui-bad);font-size:14px;">' +
      'Couldn\u2019t load reports. ' + escapeHtml(e.message) + '</div>';
  }
}

// Area-manager / admin view: launcher + recent visits.
async function renderAreaUserVisits(container) {
  injectSvrStyles();
  container.innerHTML =
    '<button type="button" id="svr-new-visit" class="svr-btn-primary" style="margin-bottom:18px;">+ New Store Visit</button>' +
    '<div class="svr-section-title">Recent visits</div>' +
    '<div id="svr-recent-list"><div style="color:var(--sub,#6b7280);font-size:14px;">Loading…</div></div>';

  const newBtn = container.querySelector('#svr-new-visit');
  if (newBtn) newBtn.addEventListener('click', function () {
    if (typeof window.showVisitChooser === 'function') {
      window.showVisitChooser();
    } else {
      alert(tr('Visit form is still loading — please try again in a moment.'));
    }
  });

  const listEl = container.querySelector('#svr-recent-list');
  try {
    const all = await fetchVisits(null);          // all stores for this AM
    renderCardList((all || []).slice(0, 25), listEl);
  } catch (e) {
    if (e.message === 'NOT_AUTHED') { authPrompt(listEl); return; }
    listEl.innerHTML = '<div style="color:var(--ui-bad);font-size:14px;">Couldn\u2019t load recent visits. ' +
      escapeHtml(e.message) + '</div>';
  }
}

// Role-based dispatcher — called when the Visits tab is opened.
export function renderVisitsScreen(container) {
  if (!container) {
    console.warn('[storeVisitView] renderVisitsScreen: container missing');
    return;
  }
  const u = getCurrentUser();
  if (!u) { authPrompt(container); return; }
  if (isAreaUser()) {
    renderAreaUserVisits(container);
  } else {
    renderStoreVisitReports(u.store, container);
  }
}

// Convenience entry point for the nav tab. switchScreen() shows/hides the
// screen by convention but has no per-screen render hook for 'visits', so
// this wrapper does both — keeps dist/ui.js untouched.
export function openVisitsTab() {
  // Defensive: the visits screen must live INSIDE #app (which is min-height:100vh).
  // If the markup placed it as a sibling AFTER #app, it renders a full viewport
  // down (the "scroll past the empty gap" bug). Re-home it into #app once —
  // works regardless of where the HTML put it, so no manual markup move needed.
  var app = document.getElementById('app');
  var vs = document.getElementById('visitsScreen');
  if (app && vs && vs.parentNode !== app) app.appendChild(vs);
  if (typeof window.switchScreen === 'function') window.switchScreen('visits');
  renderVisitsScreen(document.getElementById('visits-screen-body'));
}

// Window re-exposure (per ELC Shift module convention)
window.openVisitsTab = openVisitsTab;
window.renderVisitsScreen = renderVisitsScreen;
window.renderStoreVisitReports = renderStoreVisitReports;
window.openStoreVisitDetail = openVisitDetail;

// Refresh the visits list when a report is submitted from the form overlay,
// so the new report appears immediately on return (no reload needed).
window.addEventListener('svr:saved', function () {
  const body = document.getElementById('visits-screen-body');
  if (body) renderVisitsScreen(body);
});
